import { useState } from "react";
import { CheckCircle2, Loader2, Phone, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useExperience } from "@/context/ExperienceContext";
import { GHL, submitLead } from "@/integrations/ghl";
import { formatApiError } from "@/lib/api";
import { BUSINESS } from "@/config/business";
import { EligibilityQuiz } from "./EligibilityQuiz";

const AGE = ["Infant", "Toddler", "Preschool", "School-age"];
const BASE = [
  { k: "parent_name", label: "Your name", req: true },
  { k: "phone", label: "Phone", type: "tel", req: true },
  { k: "email", label: "Email", type: "email" },
];

export const FORMS = {
  enroll: {
    title: "Enroll Now", sub: "Tell us a little about your family and RR&B will reach out about enrollment.",
    fields: [...BASE, { k: "child_age", label: "Child's age", options: AGE },
      { k: "care_need", label: "Care needed", options: ["Daytime", "Evening", "Overnight", "Weekends", "Not sure yet"] },
      { k: "preferred_date", label: "Ideal start date", type: "date" }, { k: "notes", label: "Anything we should know?", area: true }],
  },
  tour: {
    title: "Schedule a Tour", sub: "Pick a time that works and RR&B will confirm your visit.",
    calendar: true,
    fields: [...BASE, { k: "child_age", label: "Child's age", options: AGE },
      { k: "preferred_date", label: "Preferred day", type: "date" },
      { k: "preferred_time", label: "Preferred time", options: ["Morning", "Afternoon", "Evening"] }],
  },
  transportation: {
    title: "Transportation Request", sub: "Transportation is available. Share your area and we'll follow up.",
    note: "Route details coming soon",
    fields: [...BASE, { k: "child_age", label: "Child's age", options: AGE },
      { k: "pickup_area", label: "Neighborhood or ZIP code" }, { k: "notes", label: "Pickup / drop-off times you need", area: true }],
  },
  callback: {
    title: "Request a Call Back", sub: "Leave your number and RR&B will call you.",
    fields: [...BASE.slice(0, 2), { k: "best_time", label: "Best time to call", options: ["Morning", "Afternoon", "Evening", "Late night"] }],
  },
};

function Field({ f, value, onChange, type }) {
  const common = { "data-testid": `${type}-input-${f.k}`, value: value || "", onChange: (e) => onChange(e.target.value) };
  return (
    <label className={`rrb-field ${f.area ? "sm:col-span-2" : ""}`}>
      <span>{f.label}{f.req && " *"}</span>
      {f.options ? (
        <select {...common}><option value="">Choose…</option>{f.options.map((o) => <option key={o}>{o}</option>)}</select>
      ) : f.area ? <textarea rows={3} {...common} /> : <input type={f.type || "text"} {...common} />}
    </label>
  );
}

function CalendarPlaceholder() {
  return (
    <div data-testid="tour-calendar-placeholder" className="rrb-placeholder">
      <CalendarDays className="h-5 w-5" />
      <div><strong>Booking calendar placeholder</strong><p>The GoHighLevel tour calendar will appear here once connected.</p></div>
    </div>
  );
}

export function LeadForm({ type, scene, onDone }) {
  const cfg = FORMS[type];
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  if (GHL.embeds[type]) return <iframe title={cfg.title} src={GHL.embeds[type]} className="h-[560px] w-full rounded-2xl" />;
  const submit = async (e) => {
    e.preventDefault();
    if (!vals.parent_name?.trim() || (vals.phone || "").replace(/\D/g, "").length < 7) {
      toast.error("Please add your name and a valid phone number.");
      return;
    }
    setBusy(true);
    try {
      await submitLead({ form_type: type, scene, ...vals, email: vals.email || null });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) return (
    <div data-testid={`${type}-form-success`} className="py-4">
      <CheckCircle2 className="h-10 w-10 text-emerald-500" />
      <h3 className="mt-3 font-display text-2xl font-semibold">Thank you!</h3>
      <p className="mt-1 opacity-80">The RR&B team received your request and will follow up soon.</p>
      <button data-testid={`${type}-form-close-btn`} onClick={onDone} className="rrb-btn rrb-btn-sky mt-5">Back to the story</button>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid={`${type}-form`} className="grid gap-3 sm:grid-cols-2">
      {cfg.note && <span className="rrb-soon sm:col-span-2 !mt-0 w-fit">{cfg.note}</span>}
      {cfg.calendar && <div className="sm:col-span-2"><CalendarPlaceholder /></div>}
      {cfg.fields.map((f) => <Field key={f.k} f={f} type={type} value={vals[f.k]} onChange={(v) => setVals({ ...vals, [f.k]: v })} />)}
      <div className="sm:col-span-2 mt-2">
        <button data-testid={`${type}-form-submit-btn`} disabled={busy} className="rrb-btn rrb-btn-primary w-full justify-center">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send request
        </button>
      </div>
    </form>
  );
}

function CallPanel() {
  const { open } = useExperience();
  return (
    <div data-testid="call-panel">
      <div className="rrb-placeholder">
        <Phone className="h-5 w-5" />
        <div>
          <strong data-testid="call-placeholder-number">{BUSINESS.phoneDisplay}</strong>
          <p>Placeholder number — RR&B's real phone number will be added here.</p>
        </div>
      </div>
      <button data-testid="call-request-callback-btn" onClick={() => open("callback")} className="rrb-btn rrb-btn-navy mt-4 w-full justify-center">
        Request a call back instead
      </button>
    </div>
  );
}

export function LeadModals() {
  const { modal, close, currentTag } = useExperience();
  const isQuiz = modal === "eligibility";
  const cfg = FORMS[modal];
  const title = isQuiz ? "Check Eligibility" : modal === "call" ? "Call RR&B" : cfg?.title;
  const sub = isQuiz ? "A few quick questions to see if your family may qualify for FREE child care." : modal === "call" ? "Talk to the RR&B team." : cfg?.sub;
  return (
    <Dialog open={!!modal} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid={`modal-${modal || "none"}`} className="rrb-modal max-h-[92svh] overflow-y-auto sm:max-w-xl">
        <div className="flex items-center gap-3">
          <img src="/media/rrb-emblem-sm.png" alt="" className="h-11 w-auto" />
          <div>
            <DialogTitle className="font-display text-2xl font-semibold">{title}</DialogTitle>
            <DialogDescription className="text-sm opacity-75">{sub}</DialogDescription>
          </div>
        </div>
        {isQuiz && <EligibilityQuiz key="modal-quiz" scene={currentTag} compact />}
        {modal === "call" && <CallPanel />}
        {cfg && <LeadForm key={modal} type={modal} scene={currentTag} onDone={close} />}
      </DialogContent>
    </Dialog>
  );
}
